import type { AgendaEvento } from '@eleg360/types';

/**
 * Grade semanal hora x dia da Agenda (proto:672-673, tokens em proto:126-128).
 *
 * A tela anterior empilhava os compromissos do dia numa caixa sem eixo
 * temporal: o das 9h e o das 17h ficavam colados, e a duração não aparecia.
 * Aqui cada compromisso ocupa a faixa da sua hora, deslocado pelos minutos e
 * com altura proporcional à duração, como no protótipo.
 */

/** Altura de uma faixa de hora — `.cal .hr`/`.cal .c` em tokens.css. */
export const ALTURA_FAIXA = 52;

/** Horário comercial exibido mesmo quando a semana está vazia. */
export const HORA_INICIO_PADRAO = 8;
export const HORA_FIM_PADRAO = 18;

const DIAS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex'];

/** Segunda-feira da semana que contém `base`. */
export function segundaDaSemana(base: Date): Date {
  const d = new Date(base);
  d.setHours(0, 0, 0, 0);
  // getDay(): 0=domingo. Domingo pertence à semana que começou na segunda anterior.
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

const mesmoDia = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Faixas de hora que a grade precisa ter.
 *
 * O protótipo cravava `[8,9,10,11,12,14,15,16,18]` — uma lista de demonstração
 * que pulava 13h e 17h e esconderia qualquer compromisso nesses horários. Aqui
 * a faixa parte do horário comercial e se ESTENDE até conter todo evento real
 * da semana; nenhum compromisso pode ficar fora da grade sem ser visto.
 */
export function faixasDeHora(eventos: AgendaEvento[]): number[] {
  let inicio = HORA_INICIO_PADRAO;
  let fim = HORA_FIM_PADRAO;
  for (const e of eventos) {
    const ini = new Date(e.inicio);
    const f = new Date(e.fim);
    // A última faixa ocupada: um evento que termina às 17:00 em ponto não ocupa a faixa das 17h.
    const ultima = f.getMinutes() > 0 ? f.getHours() : f.getHours() - 1;
    inicio = Math.min(inicio, ini.getHours());
    fim = Math.max(fim, ini.getHours(), ultima);
  }
  return Array.from({ length: fim - inicio + 1 }, (_, i) => inicio + i);
}

export interface GradeSemanalProps {
  eventos: AgendaEvento[];
  /** Qualquer data dentro da semana exibida. */
  semana: Date;
  aoAbrirEvento?: (e: AgendaEvento) => void;
  /** Duplo clique numa célula: novo compromisso naquele dia e naquela hora. */
  aoNovoCompromisso?: (dia: Date, hora: number) => void;
}

export function GradeSemanal({ eventos, semana, aoAbrirEvento, aoNovoCompromisso }: GradeSemanalProps) {
  const segunda = segundaDaSemana(semana);
  const dias = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(segunda);
    d.setDate(d.getDate() + i);
    return d;
  });
  const sabado = new Date(segunda);
  sabado.setDate(sabado.getDate() + 5);
  const domingo = new Date(segunda);
  domingo.setDate(domingo.getDate() + 6);

  const naSemana = eventos.filter((e) => {
    const d = new Date(e.inicio);
    return d >= segunda && d < new Date(domingo.getTime() + 24 * 3600_000);
  });
  const naGrade = naSemana.filter((e) => dias.some((d) => mesmoDia(new Date(e.inicio), d)));
  const fimDeSemana = naSemana.filter((e) => !naGrade.includes(e));

  const horas = faixasDeHora(naGrade);
  const hoje = new Date();

  const daCelula = (iDia: number, hora: number) =>
    naGrade.filter((e) => {
      const d = new Date(e.inicio);
      return mesmoDia(d, dias[iDia]!) && d.getHours() === hora;
    });

  return (
    <>
      <div className="cal">
        <div className="h" />
        {dias.map((d, i) => {
          const ehHoje = mesmoDia(d, hoje);
          return (
            <div
              key={i}
              className="h"
              data-testid={`dia-${i}`}
              style={ehHoje ? { color: 'var(--accent)' } : undefined}
            >
              {DIAS[i]}
              <small>
                {d.getDate()}
                {ehHoje ? ' · hoje' : ''}
              </small>
            </div>
          );
        })}

        {horas.map((h) => (
          <FaixaDeHora
            key={h}
            hora={h}
            dias={dias}
            eventosDa={daCelula}
            aoAbrirEvento={aoAbrirEvento}
            aoNovoCompromisso={aoNovoCompromisso}
          />
        ))}
      </div>

      {fimDeSemana.length > 0 && (
        <div className="note warn" style={{ marginTop: 8 }} data-testid="fora-da-grade">
          <b>Fora da grade de segunda a sexta</b>
          {fimDeSemana.length} compromisso(s) desta semana caem no fim de semana e não aparecem
          acima:{' '}
          {fimDeSemana
            .map(
              (e) =>
                `${new Date(e.inicio).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit' })} · ${e.titulo}`,
            )
            .join(' · ')}
        </div>
      )}
    </>
  );
}

function FaixaDeHora({
  hora,
  dias,
  eventosDa,
  aoAbrirEvento,
  aoNovoCompromisso,
}: {
  hora: number;
  dias: Date[];
  eventosDa: (iDia: number, hora: number) => AgendaEvento[];
  aoAbrirEvento?: (e: AgendaEvento) => void;
  aoNovoCompromisso?: (dia: Date, hora: number) => void;
}) {
  return (
    <>
      <div className="hr">{hora}h</div>
      {dias.map((dia, iDia) => (
        <div
          key={iDia}
          className="c"
          data-testid={`cel-${iDia}-${hora}`}
          onDoubleClick={() => aoNovoCompromisso?.(new Date(dia), hora)}
        >
          {eventosDa(iDia, hora).map((e) => {
            const ini = new Date(e.inicio);
            const duracaoH = (new Date(e.fim).getTime() - ini.getTime()) / 3600_000;
            return (
              <button
                key={e.id}
                type="button"
                data-testid={`ev-${e.id}`}
                className={`ev ${e.publico ? 'g' : 'w'}`}
                style={{
                  top: (ini.getMinutes() / 60) * ALTURA_FAIXA,
                  height: Math.max(26, duracaoH * ALTURA_FAIXA - 4),
                }}
                title={`${e.titulo} · ${e.endereco || e.bairro || 'sem local'}${e.versao > 1 ? ` · v${e.versao}` : ''}`}
                onClick={(ev) => {
                  ev.stopPropagation();
                  aoAbrirEvento?.(e);
                }}
              >
                {e.titulo}
                <small>{e.endereco || e.bairro || ''}</small>
              </button>
            );
          })}
        </div>
      ))}
    </>
  );
}
